/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import { Formik, type FormikHelpers, type FormikProps } from 'formik'
import { isEqual } from 'lodash'
import { useMemo, type FC } from 'react'
import { Button, Col, FloatingLabel, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

export const validationSchema = yup.object().shape({
  id: yup.string(),
  page: yup.string().min(3).max(255).nullable(),
  description: yup.string().min(3).max(255).nullable(),
  preview: yup.string().nullable(),
  height: yup.number().positive().integer().nullable(),
  width: yup.number().positive().integer().nullable(),
  mobileHeight: yup.number().positive().integer().nullable(),
  mobileWidth: yup.number().positive().integer().nullable(),
  conditions: yup.mixed().nullable().transform(value => {
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']).default('active'),
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormBannerLocationHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormBannerLocationHandleSubmit,
  initialValues?: Partial<ValidationSchema>
}

const FormBannerLocation: FC<FormikProps<ValidationSchema>> = ({
  handleSubmit,
  handleChange,
  handleBlur,
  isSubmitting,
  isValid,
  values,
  errors,
  initialValues = {},
}) => {
  const navigate = useNavigate()

  const isChanged: boolean = useMemo(() => {
    return !isEqual(values, initialValues)
  }, [values, initialValues])

  return (
    <Row>
      <Col md={12} className='mb-3'>
        <form onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} md={2} className="mb-3">
              <FloatingLabel label="Código">
                <Form.Control
                  type='text'
                  name='id'
                  value={values.id ?? ''}
                  placeholder='O id será gerado automaticamente'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled
                />
              </FloatingLabel>
              {errors.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={5} className="mb-3">
              <FloatingLabel label="Descrição">
                <Form.Control
                  type='text'
                  name='description'
                  value={values.description ?? ''}
                  placeholder='Descrição do local do banner'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
              </FloatingLabel>
              {errors.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={5} className="mb-3">
              <FloatingLabel label="Página">
                <Form.Control
                  type='text'
                  name='page'
                  value={values.page ?? ''}
                  placeholder='Nome da página onde o banner será exibido'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
              </FloatingLabel>
              {errors.page && <p>{errors.page}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className="mb-3">
              <FloatingLabel label="Largura em pixels">
                <Form.Control
                  type='number'
                  name='width'
                  value={values.width ?? ''}
                  placeholder='Descrição do local do banner'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
              </FloatingLabel>
              {errors.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className="mb-3">
              <FloatingLabel label="Altura em pixels">
                <Form.Control
                  type='number'
                  name='height'
                  value={values.height ?? ''}
                  placeholder='Descrição do local do banner'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
              </FloatingLabel>
              {errors.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className="mb-3">
              <FloatingLabel label="Largura (mobile) em pixels">
                <Form.Control
                  type='number'
                  name='mobileWidth'
                  value={values.mobileWidth ?? ''}
                  placeholder='Descrição do local do banner'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
              </FloatingLabel>
              {errors.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={3} className="mb-3">
              <FloatingLabel label="Altura (mobile) em pixels">
                <Form.Control
                  type='number'
                  name='mobileHeight'
                  value={values.mobileHeight ?? ''}
                  placeholder='Descrição do local do banner'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
              </FloatingLabel>
              {errors.id && <p>{errors.id}</p>}
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <FloatingLabel label="Prévia do local do banner em base64">
                <Form.Control
                  as="textarea"
                  rows={5}
                  style={{ height: '150px' }}
                  name='preview'
                  placeholder="Prévia do banner"
                  value={values.preview ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group as={Col} md={12} className="mb-3">
              <OverlayTrigger
                placement="right"
                delay={{ show: 250, hide: 400 }}
                overlay={(props) => (
                  <Tooltip {...props}>
                    Essa é uma string JSON utilizada pelo site para identificar o local do banner.
                  </Tooltip>
                )}
              >
                <FloatingLabel label="Condições de ativação">
                  <Form.Control
                    as="textarea"
                    rows={5}
                    style={{ height: '150px' }}
                    name='conditions'
                    disabled={isSubmitting}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Metadados associados ao evento"
                    value={typeof values.conditions === 'string'
                      ? values.conditions
                      : JSON.stringify(values.conditions, null, 2)
                    }
                  />
                </FloatingLabel>
              </OverlayTrigger>
            </Form.Group>
            <Col md={12}>
              <Button
                type="submit"
                variant='primary'
                disabled={!isValid || isSubmitting || !isChanged}
              >
                Salvar postagem
              </Button>
              <Button
                variant="secondary"
                style={{ marginLeft: 10 }}
                onClick={() => navigate('/banners/locations')}
                disabled={isSubmitting}
              >
                Voltar
              </Button>
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  )
}

const FormBannerLocationWrapper: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {}
}) => (
  <Formik
    component={FormBannerLocation}
    validateOnChange
    validateOnBlur
    validateOnMount
    enableReinitialize
    validationSchema={validationSchema}
    initialValues={validationSchema.cast(initialValues)}
    onSubmit={onSubmit}
  />
)

export default FormBannerLocationWrapper
